import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { FEATURE_FLAGS, useFeatureFlag } from '@platform/feature-flags';
import { Icon } from '@shared/ui';
import clsx from 'clsx';
import type { FC, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

type UserNavProps = {
  isExpanded: boolean;
  shellMode?: 'coach' | 'member';
  lightTheme?: boolean;
};

type UserNavItem = {
  activeMatcher: (pathname: string) => boolean;
  badge?: string;
  icon: string;
  label: string;
  route: string;
};

const coachNavItems = (
  showBetaBadge: boolean,
  // showMemberBadge: boolean,
  showManualTranscripts: boolean
): readonly UserNavItem[] => [
  {
    activeMatcher: (pathname: string) =>
      pathname === '/members' ||
      pathname.startsWith('/members/') ||
      pathname.startsWith('/member/') ||
      pathname === '/drive1on1/summary' ||
      pathname.startsWith('/drive1on1/summary/'),
    badge: showBetaBadge ? 'Beta' : undefined,
    icon: 'groups',
    label: 'Members',
    route: '/members',
  },
  // MEN-4464: keep the old All Artifacts entry easy to restore while the route is disabled.
  // {
  //   activeMatcher: (pathname: string) =>
  //     pathname === '/documents' || pathname.startsWith('/documents/'),
  //   badge: showMemberBadge ? 'New' : undefined,
  //   icon: 'folder',
  //   label: 'All Artifacts',
  //   route: '/documents',
  // },
  ...(showManualTranscripts
    ? [
        {
          activeMatcher: (pathname: string) =>
            pathname === '/manual-transcripts' || pathname.startsWith('/manual-transcripts/'),
          icon: 'edit_note',
          label: 'Manual Transcripts',
          route: '/manual-transcripts',
        },
      ]
    : []),
  {
    activeMatcher: (pathname: string) =>
      pathname === '/settings/calendars' || pathname.startsWith('/settings/calendars/'),
    icon: 'calendar_month',
    label: 'Calendars',
    route: '/settings/calendars',
  },
];

const memberNavItems: readonly UserNavItem[] = [];

const renderBadge = (badge: string | undefined, colorClassName = 'bg-ocean-500'): ReactNode => {
  if (!badge) {
    return null;
  }

  return (
    <span
      className={clsx(
        'absolute -top-1 -right-1 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full',
        colorClassName
      )}
    >
      {badge}
    </span>
  );
};

const UserNav: FC<UserNavProps> = ({ isExpanded, shellMode = 'coach', lightTheme = false }) => {
  const location = useLocation();
  const showBetaBadge = useFeatureFlag(FEATURE_FLAGS.TEST_FLAG);
  // const showMemberBadge = useFeatureFlag(FEATURE_FLAGS.MEMBER_TEST_FLAG);
  const showManualTranscripts = useFeatureFlag(FEATURE_FLAGS.MANUAL_DRIVE_ARTIFACT_GENERATION);
  const navItems =
    shellMode === 'member' ? memberNavItems : coachNavItems(showBetaBadge, showManualTranscripts);

  if (!isExpanded) {
    return (
      <>
        {navItems.map((item) => {
          const isActive = item.activeMatcher(location.pathname);

          return (
            <Tooltip key={item.route} placement="right" title={item.label}>
              <Link
                aria-current={isActive ? 'page' : undefined}
                aria-label={item.label}
                className="relative"
                to={item.route}
              >
                <IconButton
                  className={clsx('rounded-2xl w-12 h-12 transition-colors', {
                    [lightTheme ? 'bg-sunny-100 text-black' : 'bg-sage-400 text-black']: isActive,
                    [lightTheme
                      ? 'text-cement-500 hover:bg-black/5'
                      : 'text-sage-100 hover:bg-white/10']: !isActive,
                  })}
                >
                  <Icon>{item.icon}</Icon>
                </IconButton>
                {renderBadge(item.badge)}
              </Link>
            </Tooltip>
          );
        })}
      </>
    );
  }

  return (
    <nav aria-label="User navigation" className="flex w-full flex-col gap-3">
      {navItems.map((item) => {
        const isActive = item.activeMatcher(location.pathname);

        return (
          <Link
            key={item.route}
            aria-current={isActive ? 'page' : undefined}
            aria-label={item.label}
            className={clsx(
              'flex w-full items-center gap-3 rounded-[14px] p-2 no-underline hover:no-underline transition-colors relative',
              isActive
                ? lightTheme
                  ? 'bg-sunny-100'
                  : 'bg-sage-400'
                : lightTheme
                  ? 'hover:bg-black/5'
                  : 'hover:bg-white/10'
            )}
            to={item.route}
          >
            <div
              className={clsx(
                'flex size-10 items-center justify-center rounded-[14px]',
                isActive
                  ? lightTheme
                    ? 'bg-sunny-100 text-black'
                    : 'bg-sage-400 text-black'
                  : lightTheme
                    ? 'text-cement-400'
                    : 'text-sage-200'
              )}
            >
              <Icon
                className={clsx(
                  isActive ? 'text-black' : lightTheme ? 'text-cement-400' : 'text-sage-200'
                )}
              >
                {item.icon}
              </Icon>
            </div>
            <span
              className={clsx(
                'text-sm font-medium no-underline',
                isActive ? 'text-black' : lightTheme ? 'text-cement-500' : 'text-white'
              )}
            >
              {item.label}
            </span>
            {renderBadge(item.badge)}
          </Link>
        );
      })}
    </nav>
  );
};

export default UserNav;
